/**
 * Wo man zuletzt war — damit die Anwendung dort wieder aufmacht.
 *
 * Aus dem Betrieb: die Web-App auf dem iPhone nimmt beim Öffnen von sich aus
 * die zuletzt besuchte Seite wieder auf, das iPad beginnt bei der Übersicht.
 * Zwei Geräte, zwei Verhalten, keines davon gewollt. Und weil sich die alte
 * Adresse auf eine gelöschte Reise bezog, stand da „Diese Reise gibt es
 * nicht" — eine Sackgasse gleich beim Öffnen.
 *
 * Gemerkt wird deshalb hier, im Gerät, und nicht auf dem Server: wo man
 * zuletzt war, ist Ansichtssache dieses Geräts. Wer am Telefon in einer Reise
 * schreibt und am Rechner in einer anderen liest, will nicht, dass ihm das
 * eine das andere umstellt.
 *
 * Gemerkt werden **Chats und die Übersicht**, sonst nichts. Wer zuletzt die
 * Liste offen hatte, soll sie wiedersehen; wer im Chat war, den Chat.
 * Einstellungen, Reiseseite und Formulare gehören nicht dazu — nach einem
 * Neustart in „Reise anlegen" zu landen wäre keine Hilfe, und die
 * Einstellungen sind kein Ort, an dem man wohnt.
 */
const MARKE = 'tb.letzter-chat'

/**
 * Die Wege, an denen man sich aufhält: der Reise-Chat, ein Antwortverlauf --
 * und die Karte. Sie ist eine zweite Ansicht derselben Reise, kein Formular:
 * wer sie offen hatte, will sie wiedersehen.
 */
const CHAT = /^\/t\/\d+(\/with\/\d+|\/karte)?$/

export function istChat(pfad: string): boolean {
  return CHAT.test(pfad)
}

/**
 * Wohin beim Start — gelesen **beim Laden des Moduls**, also vor dem ersten
 * Rendern.
 *
 * Der Zeitpunkt ist keine Feinheit, sondern der Kern: `Shell` schreibt den Ort
 * in einem Effekt mit, und Effekte laufen nach dem Rendern. Ohne diese frühe
 * Kopie hinge das Ergebnis daran, wer zuerst drankommt — und beim Neuladen
 * durch die `StandWache` hätte die verworfene erste Runde bereits „Übersicht"
 * hinterlassen. Genau so kam auf dem iPhone die Liste, obwohl zuletzt ein Chat
 * offen war.
 */
let beimStart = lies()

export type Startziel = { art: 'chat'; pfad: string } | { art: 'liste' } | null

function lies(): Startziel {
  try {
    const pfad = localStorage.getItem(MARKE)
    // Geprüft wird auch beim Lesen: was hier steht, geht ungefragt in die
    // Adresszeile, und ein alter oder verbogener Eintrag hat dort nichts zu
    // suchen.
    if (pfad !== null && istChat(pfad)) return { art: 'chat', pfad }
    if (pfad === '/') return { art: 'liste' }
    return null
  } catch {
    return null
  }
}

export function startziel(): Startziel {
  return beimStart
}

/**
 * Was **jetzt** im Speicher steht — nicht, was beim Öffnen dort stand.
 *
 * Nur für die Standzeile unter den Einstellungen, und dort mit Bedacht: fallen
 * die beiden auseinander, ist beim Öffnen etwas anderes gelesen worden als
 * inzwischen geschrieben wurde. Stehen beide auf „nichts", kommt das Schreiben
 * gar nicht durch. Aus dem Betrieb ist genau das offen — am iPhone wird nichts
 * behalten, am iPad schon — und von außen sind die beiden Fälle nicht zu
 * unterscheiden.
 */
export function gemerkterOrt(): Startziel {
  return lies()
}

export function merkeOrt(pfad: string): void {
  if (pfad !== '/' && !istChat(pfad)) return
  try {
    localStorage.setItem(MARKE, pfad)
  } catch {
    // Nicht merken zu können ist kein Grund, nicht zu bedienen.
  }
}

/**
 * Vergessen — und zwar beides, den Eintrag und die frühe Kopie.
 *
 * Gebraucht beim Abmelden und dann, wenn die gemerkte Reise verschwunden ist.
 * Bliebe die Kopie stehen, schickte sie einen geradewegs dorthin zurück, wo es
 * nichts mehr gibt.
 */
export function vergissOrt(): void {
  beimStart = null
  try {
    localStorage.removeItem(MARKE)
  } catch {
    // Siehe oben.
  }
}
